/**
 * A page opened before a deploy asks for chunks of the OLD build, which the server no longer has (their names carry a
 * hash). The lazy screen then fails to load and the error screen's «Попробовать снова» cannot help — only a reload
 * brings the new build. So: recognise that failure and reload once (never in a loop: one try per minute per tab).
 *
 * Review round 3: a dropped network (a phone on a flaky line, the family server's computer asleep) fails a chunk with
 * the SAME message. Reloading then lands the child on the browser's own offline page, and the app stays gone when the
 * network comes back. So the page reloads only when the server answers (`serverAnswers`: the failed chunk's URL, else
 * /api/health — any HTTP answer); offline the in-app «Ой! Что-то запуталось» card stays (the shell, «На главную» and
 * Гамбитик keep working) and the reload waits for the window's 'online' event (`whenOnline`).
 */

const RELOAD_MARK = 'gambit.staleBuildReload';
const RELOAD_GAP_MS = 60_000;

/** Is this the failure of loading a code chunk (a stale build), not a bug of the screen itself? */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|is not a valid JavaScript MIME type|MIME type of "text\/html"/i.test(error.message);
}

interface ReloadDeps {
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  now: () => number;
  reload: () => void;
}

function browserDeps(): ReloadDeps {
  let storage: ReloadDeps['storage'] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return { storage, now: () => Date.now(), reload: () => window.location.reload() };
}

/** Reload the page for the new build unless this tab already did within the last minute. true = reloading. */
export function reloadForNewBuild(deps: ReloadDeps = browserDeps()): boolean {
  const now = deps.now();
  try {
    const mark = deps.storage?.getItem(RELOAD_MARK) ?? null;
    const last = mark === null ? Number.NaN : Number(mark);
    if (Number.isFinite(last) && now - last < RELOAD_GAP_MS) return false;
    deps.storage?.setItem(RELOAD_MARK, String(now));
  } catch {
    // no storage: still reload once — the page that comes back is the new build
  }
  deps.reload();
  return true;
}

/** The chunk a failed dynamic import names (Chrome: «Failed to fetch dynamically imported module: <url>»), else null. */
export function failedChunkUrl(error: unknown): string | null {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const m = /(https?:\/\/[^\s'"]+?\.(?:m?js|css))(?:[?#][^\s'"]*)?/i.exec(message);
  return m ? (m[1] as string) : null;
}

export interface ProbeDeps {
  /** false = the browser knows it is offline */
  online(): boolean;
  /** any HTTP answer resolves; no answer (offline, the server down) rejects */
  fetch(url: string): Promise<unknown>;
}

/** A probe that gets no answer this long counts as no answer (a half-dead network must not keep the spinner up). */
export const PROBE_TIMEOUT_MS = 4000;

function browserProbe(): ProbeDeps {
  return {
    online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
    fetch: (url) => {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
      return fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: abort.signal }).finally(() => clearTimeout(timer));
    },
  };
}

/** Does the server answer now (any HTTP status: a missing chunk's 404 too)? Offline or a fetch that throws: no. */
export async function serverAnswers(url: string | null, deps: ProbeDeps = browserProbe()): Promise<boolean> {
  if (!deps.online()) return false;
  try {
    await deps.fetch(url ?? '/api/health');
    return true;
  } catch {
    return false;
  }
}

/**
 * A chunk failed to load: reload for the new build — only when the server answers (see the header). true = reloading
 * now; false = offline (or reloaded within the last minute): the caller keeps its card and may wait for `whenOnline`.
 */
export async function reloadWhenServerAnswers(error: unknown, deps: { probe?: ProbeDeps; reload?: ReloadDeps } = {}): Promise<boolean> {
  if (!(await serverAnswers(failedChunkUrl(error), deps.probe))) return false;
  return reloadForNewBuild(deps.reload);
}

/** Runs `fn` once when the window comes back online; returns the clean-up. */
export function whenOnline(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const on = (): void => {
    window.removeEventListener('online', on);
    fn();
  };
  window.addEventListener('online', on);
  return () => window.removeEventListener('online', on);
}

/**
 * Vite's own signal that a preloaded chunk failed (`vite:preloadError`): a stale build reloads — once the server is
 * known to answer. The event is not cancelled (review round 3): offline, the import fails on to the screen's error
 * boundary (the in-app card that reloads when the network is back) instead of resolving to nothing.
 */
export function watchStaleBuild(): void {
  window.addEventListener('vite:preloadError', (event) => {
    void reloadWhenServerAnswers((event as Event & { payload?: unknown }).payload);
  });
}
